/**
 * InstallationsFocusPage — Phase BB3b (2026-05-23)
 *
 * Admin review surface for AI-proposed focus boxes on TemplateInstallation
 * photos (set by sibling BB2 via Qwen2.5-VL). Karan/Mamta scan a grid of
 * installations, eyeball the AI suggestions (dashed red rectangle overlay),
 * and either approve, edit, or reset each one.
 *
 * Endpoints consumed:
 *   GET    /api/v1/admin/installations
 *   PUT    /api/v1/admin/installations/:id/focus-box
 *   POST   /api/v1/admin/installations/:id/approve-ai
 *   POST   /api/v1/admin/installations/bulk-approve
 *
 * Design: AURA tokens — var(--saffron), var(--ink-2), etc. No bg-primary-* Tailwind.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Camera, Check, RotateCcw, Edit3, X, Search } from 'lucide-react';
import { api } from '../../utils/api';

// ─── Types (mirror backend) ──────────────────────────────────────────────────

interface FocusBox { x: number; y: number; w: number; h: number }

interface Installation {
  id: string;
  category: string;
  projectName: string;
  state: string | null;
  city: string | null;
  year: number | null;
  publicUrl: string;
  focusBox: FocusBox | null;
  focusSource: 'ai' | 'manual' | null;
  focusConfidence: number | null;
  focusReviewed: boolean;
  focusUpdatedAt: string | null;
}

type ReviewedFilter = 'all' | 'unreviewed' | 'lowConfidence' | 'aiOnly';

const CATEGORIES = [
  'SS_WATER_TANK',
  'DOMESTIC_HEAT_PUMP',
  'COMMERCIAL_HEAT_PUMP',
  'POOL_HEAT_PUMP',
  'SOLAR_WATER_HEATER',
  'HYBRID_HEAT_PUMP',
  'SOLAR_SPACE_HEATING',
] as const;

// ─── Page ────────────────────────────────────────────────────────────────────

export function InstallationsFocusPage() {
  const [rows, setRows] = useState<Installation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set());
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [filterReviewed, setFilterReviewed] = useState<ReviewedFilter>('all');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Installation | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/admin/installations');
      setRows(res.data?.data ?? []);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.message ?? 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filterCategory && r.category !== filterCategory) return false;
      if (filterReviewed === 'unreviewed' && r.focusReviewed) return false;
      if (filterReviewed === 'aiOnly' && r.focusSource !== 'ai') return false;
      if (
        filterReviewed === 'lowConfidence' &&
        !(r.focusSource === 'ai' && (r.focusConfidence ?? 1) < 0.7)
      ) return false;
      if (q && !r.projectName.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rows, filterCategory, filterReviewed, search]);

  const bulkCandidates = useMemo(
    () => rows.filter((r) => r.focusSource === 'ai' && !r.focusReviewed && (r.focusConfidence ?? 0) >= 0.85),
    [rows],
  );

  const markSaving = (id: string, on: boolean) => {
    setSavingIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id); else next.delete(id);
      return next;
    });
  };

  const approveAi = async (row: Installation) => {
    markSaving(row.id, true);
    try {
      const res = await api.post(`/admin/installations/${row.id}/approve-ai`);
      const updated: Partial<Installation> = res.data?.data ?? {};
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, ...updated } : r)));
    } catch (e: any) {
      alert(e?.response?.data?.error ?? 'Approve failed');
    } finally {
      markSaving(row.id, false);
    }
  };

  const resetFocus = async (row: Installation) => {
    if (!confirm('Reset to center-crop default? This clears the focus box.')) return;
    markSaving(row.id, true);
    try {
      const res = await api.put(`/admin/installations/${row.id}/focus-box`, {
        focusBox: null,
        reviewed: true,
      });
      const updated: Partial<Installation> = res.data?.data ?? {};
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, ...updated, focusBox: null, focusSource: null, focusConfidence: null } : r)));
    } catch (e: any) {
      alert(e?.response?.data?.error ?? 'Reset failed');
    } finally {
      markSaving(row.id, false);
    }
  };

  const saveEdit = async (id: string, focusBox: FocusBox) => {
    markSaving(id, true);
    try {
      const res = await api.put(`/admin/installations/${id}/focus-box`, {
        focusBox,
        reviewed: true,
      });
      const updated: Partial<Installation> = res.data?.data ?? {};
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...updated } : r)));
      setEditing(null);
    } catch (e: any) {
      alert(e?.response?.data?.error ?? 'Save failed');
    } finally {
      markSaving(id, false);
    }
  };

  const bulkApprove = async () => {
    if (!bulkCandidates.length) return;
    if (!confirm(`Approve all ${bulkCandidates.length} high-confidence AI suggestions?`)) return;
    try {
      const res = await api.post('/admin/installations/bulk-approve', {
        minConfidence: 0.85,
        ...(filterCategory ? { category: filterCategory } : {}),
      });
      const count = res.data?.data?.count ?? 0;
      alert(`Approved ${count} installation${count === 1 ? '' : 's'}.`);
      load();
    } catch (e: any) {
      alert(e?.response?.data?.error ?? 'Bulk approve failed');
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      <header style={{ marginBottom: 20 }}>
        <h1 style={{
          display: 'flex', alignItems: 'center', gap: 10,
          fontSize: 22, fontWeight: 700, margin: 0, color: 'var(--ink-1)',
        }}>
          <Camera size={22} /> Photo Focus Review
        </h1>
        <p style={{ margin: '6px 0 0 0', color: 'var(--ink-3)', fontSize: 13 }}>
          Review AI-detected focus boxes on installation photos. Edit by drag, approve to mark reviewed.
        </p>
      </header>

      {/* Filter bar */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center',
        padding: 12, marginBottom: 16,
        border: '1px solid var(--line-2)', borderRadius: 8, background: 'var(--bg-1)',
      }}>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          style={selectStyle}
        >
          <option value="">All categories</option>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>
          ))}
        </select>

        <select
          value={filterReviewed}
          onChange={(e) => setFilterReviewed(e.target.value as ReviewedFilter)}
          style={selectStyle}
        >
          <option value="all">Show: All</option>
          <option value="unreviewed">Unreviewed only</option>
          <option value="lowConfidence">Low-confidence (&lt;0.7)</option>
          <option value="aiOnly">AI only</option>
        </select>

        <div style={{ position: 'relative', flex: '1 1 240px', minWidth: 200 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: 'var(--ink-3)' }} />
          <input
            type="text"
            placeholder="Search project name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ ...inputStyle, paddingLeft: 30, width: '100%' }}
          />
        </div>

        <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--ink-3)' }}>
          {filtered.length} / {rows.length}
        </span>
      </div>

      {error && (
        <div style={{
          padding: 12, marginBottom: 12, border: '1px solid #fca5a5',
          background: '#fee2e2', color: '#991b1b', borderRadius: 6, fontSize: 13,
        }}>{error}</div>
      )}

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)' }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{
          padding: 40, textAlign: 'center', color: 'var(--ink-3)',
          border: '1px dashed var(--line-2)', borderRadius: 8,
        }}>
          {rows.length === 0 ? 'No installations yet.' : 'No installations match your filters.'}
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 16,
        }}>
          {filtered.map((row) => (
            <Card
              key={row.id}
              row={row}
              saving={savingIds.has(row.id)}
              onEdit={() => setEditing(row)}
              onApproveAi={() => approveAi(row)}
              onReset={() => resetFocus(row)}
            />
          ))}
        </div>
      )}

      {/* Bulk action bar — sticky bottom when filter narrows to AI candidates */}
      {bulkCandidates.length > 0 && filterReviewed === 'aiOnly' && (
        <div style={{
          position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)',
          padding: '10px 16px', background: 'var(--saffron, #f59e0b)', color: '#fff',
          borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
          display: 'flex', alignItems: 'center', gap: 12, zIndex: 50,
        }}>
          <Check size={16} />
          <span style={{ fontSize: 13, fontWeight: 600 }}>
            Approve all {bulkCandidates.length} high-confidence AI suggestions
          </span>
          <button onClick={bulkApprove} style={bulkBtnStyle}>Approve</button>
        </div>
      )}

      {/* Edit modal */}
      {editing && (
        <EditModal
          row={editing}
          onClose={() => setEditing(null)}
          onSave={(box) => saveEdit(editing.id, box)}
          saving={savingIds.has(editing.id)}
        />
      )}
    </div>
  );
}

// ─── Card ────────────────────────────────────────────────────────────────────

interface CardProps {
  row: Installation;
  saving: boolean;
  onEdit: () => void;
  onApproveAi: () => void;
  onReset: () => void;
}

function Card({ row, saving, onEdit, onApproveAi, onReset }: CardProps) {
  const confColor =
    row.focusConfidence == null ? '#94a3b8'
    : row.focusConfidence >= 0.85 ? '#16a34a'
    : row.focusConfidence >= 0.7 ? '#d97706'
    : '#dc2626';

  return (
    <div style={{
      border: '1px solid var(--line-2)', borderRadius: 8, overflow: 'hidden',
      background: 'var(--bg-1)', display: 'flex', flexDirection: 'column',
    }}>
      <div style={{ position: 'relative', height: 200, background: '#0f172a' }}>
        <img
          src={row.publicUrl}
          alt={row.projectName}
          loading="lazy"
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
        <FocusOverlay box={row.focusBox} source={row.focusSource} />
        {row.focusReviewed && (
          <span style={{
            position: 'absolute', top: 6, right: 6, padding: '2px 8px',
            background: '#16a34a', color: '#fff', borderRadius: 999,
            fontSize: 11, fontWeight: 600,
          }}>Reviewed</span>
        )}
      </div>
      <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
        <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--ink-1)' }}>
          {row.projectName}
        </div>
        <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
          {[row.city, row.state, row.year].filter(Boolean).join(' · ') || '—'}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
          <Badge>{row.category.replace(/_/g, ' ')}</Badge>
          {row.focusSource && (
            <Badge color={row.focusSource === 'manual' ? '#7c3aed' : '#0284c7'}>
              {row.focusSource}
            </Badge>
          )}
          {row.focusConfidence != null && (
            <Badge color={confColor}>
              {Math.round(row.focusConfidence * 100)}%
            </Badge>
          )}
          {!row.focusBox && <Badge color="#64748b">no focus</Badge>}
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 'auto', paddingTop: 8 }}>
          <button onClick={onEdit} disabled={saving} style={smallBtnStyle}>
            <Edit3 size={12} /> Edit
          </button>
          {row.focusSource === 'ai' && !row.focusReviewed && (
            <button onClick={onApproveAi} disabled={saving} style={{ ...smallBtnStyle, color: '#16a34a' }}>
              <Check size={12} /> Approve AI
            </button>
          )}
          {row.focusBox && (
            <button onClick={onReset} disabled={saving} style={{ ...smallBtnStyle, color: '#dc2626' }}>
              <RotateCcw size={12} /> Reset
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Focus overlay (read-only SVG rectangle) ─────────────────────────────────

function FocusOverlay({ box, source }: { box: FocusBox | null; source: string | null }) {
  if (!box) return null;
  const dashed = source === 'ai';
  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      style={{
        position: 'absolute', inset: 0, width: '100%', height: '100%',
        pointerEvents: 'none',
      }}
    >
      <rect
        x={box.x} y={box.y} width={box.w} height={box.h}
        fill="none" stroke="#ef4444" strokeWidth={0.6}
        strokeDasharray={dashed ? '2 1.5' : undefined}
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

// ─── Edit modal with drag-to-resize ──────────────────────────────────────────

interface EditModalProps {
  row: Installation;
  onClose: () => void;
  onSave: (box: FocusBox) => void;
  saving: boolean;
}

type Handle =
  | 'move'
  | 'nw' | 'ne' | 'sw' | 'se'
  | 'n' | 's' | 'e' | 'w';

function EditModal({ row, onClose, onSave, saving }: EditModalProps) {
  // Default box = current, or 60% centered crop if null.
  const [box, setBox] = useState<FocusBox>(
    row.focusBox ?? { x: 20, y: 20, w: 60, h: 60 },
  );
  const surfaceRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ handle: Handle; startBox: FocusBox; startX: number; startY: number; shift: boolean } | null>(null);
  const [, setDragTick] = useState(0); // force re-render on drag

  // Convert pixel deltas (event coords) into 0-100 % deltas based on surface size.
  const pxToPctDelta = useCallback((dxPx: number, dyPx: number) => {
    const el = surfaceRef.current;
    if (!el) return { dx: 0, dy: 0 };
    const r = el.getBoundingClientRect();
    return { dx: (dxPx / r.width) * 100, dy: (dyPx / r.height) * 100 };
  }, []);

  const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

  const onPointerDown = (handle: Handle) => (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    dragRef.current = {
      handle,
      startBox: { ...box },
      startX: e.clientX,
      startY: e.clientY,
      shift: e.shiftKey,
    };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    const { dx, dy } = pxToPctDelta(e.clientX - d.startX, e.clientY - d.startY);
    const s = d.startBox;
    let next: FocusBox = { ...s };
    const MIN = 5;
    switch (d.handle) {
      case 'move': {
        next.x = clamp(s.x + dx, 0, 100 - s.w);
        next.y = clamp(s.y + dy, 0, 100 - s.h);
        break;
      }
      case 'e': next.w = clamp(s.w + dx, MIN, 100 - s.x); break;
      case 'w': {
        const newX = clamp(s.x + dx, 0, s.x + s.w - MIN);
        next.w = s.w + (s.x - newX);
        next.x = newX;
        break;
      }
      case 's': next.h = clamp(s.h + dy, MIN, 100 - s.y); break;
      case 'n': {
        const newY = clamp(s.y + dy, 0, s.y + s.h - MIN);
        next.h = s.h + (s.y - newY);
        next.y = newY;
        break;
      }
      case 'se': {
        next.w = clamp(s.w + dx, MIN, 100 - s.x);
        next.h = clamp(s.h + dy, MIN, 100 - s.y);
        break;
      }
      case 'sw': {
        const newX = clamp(s.x + dx, 0, s.x + s.w - MIN);
        next.w = s.w + (s.x - newX);
        next.x = newX;
        next.h = clamp(s.h + dy, MIN, 100 - s.y);
        break;
      }
      case 'ne': {
        next.w = clamp(s.w + dx, MIN, 100 - s.x);
        const newY = clamp(s.y + dy, 0, s.y + s.h - MIN);
        next.h = s.h + (s.y - newY);
        next.y = newY;
        break;
      }
      case 'nw': {
        const newX = clamp(s.x + dx, 0, s.x + s.w - MIN);
        const newY = clamp(s.y + dy, 0, s.y + s.h - MIN);
        next.w = s.w + (s.x - newX);
        next.h = s.h + (s.y - newY);
        next.x = newX;
        next.y = newY;
        break;
      }
    }

    // Shift-lock aspect ratio of the original box (corner handles only).
    if (e.shiftKey && (d.handle === 'nw' || d.handle === 'ne' || d.handle === 'sw' || d.handle === 'se')) {
      const ratio = s.w / s.h;
      // Pick the dimension that grew more, then derive the other.
      const dW = Math.abs(next.w - s.w);
      const dH = Math.abs(next.h - s.h);
      if (dW > dH) {
        next.h = clamp(next.w / ratio, MIN, 100);
      } else {
        next.w = clamp(next.h * ratio, MIN, 100);
      }
      // Re-clamp position so we don't run off-canvas.
      next.x = clamp(next.x, 0, 100 - next.w);
      next.y = clamp(next.y, 0, 100 - next.h);
    }

    setBox(next);
    setDragTick((t) => t + 1);
  };

  const onPointerUp = () => { dragRef.current = null; };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.7)',
        zIndex: 100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: 20, overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-1, #fff)', borderRadius: 10, maxWidth: 880, width: '100%',
          maxHeight: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}
      >
        <header style={{
          padding: '12px 16px', borderBottom: '1px solid var(--line-2)',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--ink-1)' }}>
              {row.projectName}
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
              <strong style={{ color: '#dc2626' }}>The rectangle is the part of the image that will stay visible in the PDF.</strong> Make it tight around just the product — anything outside gets cropped out. Drag to move. Drag a handle to resize. Hold Shift on corner to lock aspect ratio.
            </div>
          </div>
          <button onClick={onClose} style={{
            border: 'none', background: 'transparent', cursor: 'pointer', padding: 4,
          }}>
            <X size={18} />
          </button>
        </header>

        <div style={{ padding: 16, overflow: 'auto' }}>
          <div
            ref={surfaceRef}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            style={{
              position: 'relative', width: '100%', maxWidth: 800,
              aspectRatio: '4 / 3', margin: '0 auto',
              background: '#0f172a', userSelect: 'none', touchAction: 'none',
            }}
          >
            <img
              src={row.publicUrl}
              alt={row.projectName}
              draggable={false}
              style={{
                position: 'absolute', inset: 0, width: '100%', height: '100%',
                objectFit: 'contain', pointerEvents: 'none',
              }}
            />
            {/* BB5: dim the area OUTSIDE the focus box so users SEE what gets cropped */}
            {/* Top band */}
            <div style={{ position: 'absolute', left: 0, top: 0, width: '100%',
              height: `${box.y}%`, background: 'rgba(15,23,42,0.55)', pointerEvents: 'none' }} />
            {/* Bottom band */}
            <div style={{ position: 'absolute', left: 0, top: `${box.y + box.h}%`, width: '100%',
              height: `${100 - box.y - box.h}%`, background: 'rgba(15,23,42,0.55)', pointerEvents: 'none' }} />
            {/* Left band */}
            <div style={{ position: 'absolute', left: 0, top: `${box.y}%`,
              width: `${box.x}%`, height: `${box.h}%`,
              background: 'rgba(15,23,42,0.55)', pointerEvents: 'none' }} />
            {/* Right band */}
            <div style={{ position: 'absolute', left: `${box.x + box.w}%`, top: `${box.y}%`,
              width: `${100 - box.x - box.w}%`, height: `${box.h}%`,
              background: 'rgba(15,23,42,0.55)', pointerEvents: 'none' }} />
            {/* Move rectangle */}
            <div
              onPointerDown={onPointerDown('move')}
              style={{
                position: 'absolute',
                left: `${box.x}%`, top: `${box.y}%`,
                width: `${box.w}%`, height: `${box.h}%`,
                border: '2px solid #ef4444',
                cursor: 'move', boxSizing: 'border-box',
                background: 'transparent',
              }}
            />
            {/* 8 handles */}
            {(
              [
                ['nw', box.x, box.y, 'nwse-resize'],
                ['n',  box.x + box.w / 2, box.y, 'ns-resize'],
                ['ne', box.x + box.w, box.y, 'nesw-resize'],
                ['e',  box.x + box.w, box.y + box.h / 2, 'ew-resize'],
                ['se', box.x + box.w, box.y + box.h, 'nwse-resize'],
                ['s',  box.x + box.w / 2, box.y + box.h, 'ns-resize'],
                ['sw', box.x, box.y + box.h, 'nesw-resize'],
                ['w',  box.x, box.y + box.h / 2, 'ew-resize'],
              ] as Array<[Handle, number, number, string]>
            ).map(([h, cx, cy, cursor]) => (
              <div
                key={h}
                onPointerDown={onPointerDown(h)}
                style={{
                  position: 'absolute',
                  left: `calc(${cx}% - 6px)`,
                  top: `calc(${cy}% - 6px)`,
                  width: 12, height: 12,
                  background: '#fff', border: '2px solid #ef4444',
                  cursor, touchAction: 'none',
                }}
              />
            ))}
          </div>

          {/* BB5: live PDF tile preview — exactly mimics what the cell will look like */}
          <div style={{ marginTop: 16, display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>PDF tile preview</div>
              <div style={{
                width: 230, height: 120, border: '1px solid var(--line-2)', borderRadius: 6,
                overflow: 'hidden', background: '#f1f5f9',
              }}>
                <img
                  src={row.publicUrl}
                  alt=""
                  style={{
                    width: '100%', height: '100%', objectFit: 'cover',
                    objectPosition: `${Math.round(box.x + box.w / 2)}% ${Math.round(box.y + box.h / 2)}%`,
                  }}
                />
              </div>
              <div style={{ fontSize: 10, color: 'var(--ink-3)', marginTop: 4 }}>
                ↑ This is exactly how the photo will appear in the PDF
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Box</div>
              <div style={{ fontSize: 13, color: 'var(--ink-2)', fontFamily: 'ui-monospace, monospace' }}>
                x: {box.x.toFixed(1)}%  y: {box.y.toFixed(1)}%<br/>
                w: {box.w.toFixed(1)}%  h: {box.h.toFixed(1)}%
              </div>
              <div style={{ fontSize: 12, marginTop: 8, color: box.w * box.h > 7500 ? '#dc2626' : box.w * box.h > 4000 ? '#b45309' : '#16a34a' }}>
                {box.w * box.h > 7500
                  ? '⚠ Box too large — covers ' + Math.round(box.w * box.h / 100) + '% of image. Drag handles to make it tighter around the product.'
                  : box.w * box.h > 4000
                  ? 'Box covers ' + Math.round(box.w * box.h / 100) + '% of image — could be tighter.'
                  : '✓ Good — box covers ' + Math.round(box.w * box.h / 100) + '% of image'}
              </div>
            </div>
          </div>
        </div>

        <footer style={{
          padding: '12px 16px', borderTop: '1px solid var(--line-2)',
          display: 'flex', justifyContent: 'flex-end', gap: 8,
        }}>
          <button onClick={onClose} disabled={saving} style={ghostBtnStyle}>Cancel</button>
          <button
            onClick={() => onSave({
              x: Math.round(box.x * 10) / 10,
              y: Math.round(box.y * 10) / 10,
              w: Math.round(box.w * 10) / 10,
              h: Math.round(box.h * 10) / 10,
            })}
            disabled={saving || box.w * box.h > 7500}
            style={{
              ...primaryBtnStyle,
              opacity: (saving || box.w * box.h > 7500) ? 0.5 : 1,
              cursor: (saving || box.w * box.h > 7500) ? 'not-allowed' : 'pointer',
            }}
            title={box.w * box.h > 7500 ? 'Box too large — make it tighter around the product' : ''}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </footer>
      </div>
    </div>
  );
}

// ─── Small styling primitives ────────────────────────────────────────────────

function Badge({ children, color = '#475569' }: { children: React.ReactNode; color?: string }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 7px', borderRadius: 999,
      fontSize: 10, fontWeight: 600, color: '#fff', background: color,
      textTransform: 'uppercase', letterSpacing: 0.3,
    }}>{children}</span>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '7px 10px',
  border: '1px solid var(--line-2)',
  borderRadius: 6,
  fontSize: 13,
  background: 'var(--bg-0, #fff)',
  color: 'var(--ink-1)',
};

const selectStyle: React.CSSProperties = { ...inputStyle, minWidth: 160 };

const smallBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '5px 8px', fontSize: 11, fontWeight: 600,
  border: '1px solid var(--line-2)', borderRadius: 5,
  background: 'var(--bg-0, #fff)', color: 'var(--ink-1)', cursor: 'pointer',
};

const primaryBtnStyle: React.CSSProperties = {
  padding: '8px 16px', fontSize: 13, fontWeight: 600,
  border: 'none', borderRadius: 6,
  background: 'var(--saffron, #f59e0b)', color: '#fff', cursor: 'pointer',
};

const ghostBtnStyle: React.CSSProperties = {
  padding: '8px 16px', fontSize: 13, fontWeight: 600,
  border: '1px solid var(--line-2)', borderRadius: 6,
  background: 'transparent', color: 'var(--ink-1)', cursor: 'pointer',
};

const bulkBtnStyle: React.CSSProperties = {
  padding: '6px 14px', fontSize: 12, fontWeight: 700,
  border: 'none', borderRadius: 5,
  background: '#fff', color: 'var(--saffron, #f59e0b)', cursor: 'pointer',
};
